import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Send, Car, ExternalLink, ShieldCheck, AlertTriangle, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function directionStyle(direction) {
  if (direction === "customer_in") return { bg: "bg-white/5 border-white/10", label: "Customer", labelClass: "text-gray-400" };
  if (direction === "broker_out") return { bg: "bg-emerald-500/5 border-emerald-500/20", label: "You (555 Broker)", labelClass: "text-emerald-300" };
  return { bg: "bg-white/5 border-white/10", label: direction, labelClass: "text-gray-400" };
}

export default function BrokerReply() {
  const { leadId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setErr("Missing token"); setLoading(false); return; }
    try {
      const { data } = await api.get(`/leads/${leadId}/reply-context?token=${encodeURIComponent(token)}`);
      setCtx(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "This reply link is invalid or has expired.");
    } finally { setLoading(false); }
  }, [leadId, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!body.trim()) { toast.error("Reply cannot be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/reply?token=${encodeURIComponent(token)}`, { body });
      setSent(true);
      setBody("");
      // Reload conversation
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center text-gray-500">Loading reply portal…</div>
    );
  }
  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center">
        <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display text-3xl mb-3">Link expired or invalid</h1>
        <p className="text-gray-400 mb-8">{err}</p>
        <p className="text-xs text-gray-500 mb-6">Reply links are valid for 7 days from when the notification email was sent. Open the lead in the admin dashboard to reply from there instead.</p>
        <Link to="/admin" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">Open Admin Dashboard</Link>
      </div>
    );
  }

  const v = ctx.vehicle;
  const conversation = ctx.conversation || [];

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8 md:py-14" data-testid="broker-reply-page">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <span className="label-mono">555 Broker Reply Portal</span>
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-light tracking-tighter mb-2">
        Reply to <span className="text-white">{ctx.customer_name}</span>
      </h1>
      <p className="text-gray-400 text-sm mb-8">
        {ctx.action_label || "Garage Handoff Request"} · Received {new Date(ctx.created_at).toLocaleString()}
      </p>

      {/* Vehicle context */}
      {v && (
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 mb-6" data-testid="reply-vehicle-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-gray-400" />
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Vehicle</span>
              </div>
              <div className="font-display text-lg truncate">{v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}</div>
              {v.url && (
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-300 hover:text-white break-all inline-flex items-center gap-1 mt-1">
                  {v.url} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {v.price && (
              <div className="text-right shrink-0">
                <div className="font-display text-xl">${Number(v.price).toLocaleString()}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Asking</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multi-item (whole garage handoff) */}
      {ctx.items?.length > 0 && (
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 mb-6" data-testid="reply-items-card">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">{ctx.items.length} Vehicles in this handoff</span>
          </div>
          <div className="space-y-2">
            {ctx.items.map((it) => (
              <div key={it.id} className="flex items-start justify-between gap-3 py-2 border-t border-white/5 first:border-t-0 first:pt-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{it.title || `${it.year || ""} ${it.make || ""} ${it.model || ""}`.trim()}</div>
                  <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-300 hover:text-white truncate block">{it.url}</a>
                  {it.notes && <div className="text-[11px] text-gray-500 italic mt-0.5">&ldquo;{it.notes}&rdquo;</div>}
                </div>
                <div className="text-xs font-mono text-gray-400 shrink-0">{it.price ? `$${Number(it.price).toLocaleString()}` : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversation chronology */}
      {conversation.length > 0 && (
        <div className="mb-6" data-testid="reply-conversation">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Conversation</span>
          </div>
          <div className="space-y-3">
            {conversation.map((m) => {
              const s = directionStyle(m.direction);
              return (
                <div key={m.id} className={`p-4 rounded-2xl border ${s.bg}`} data-testid={`msg-${m.id}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className={`text-[10px] uppercase tracking-wider font-medium ${s.labelClass}`}>{s.label}</div>
                    <div className="text-[10px] text-gray-600">{new Date(m.sent_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sent && (
        <div className="mb-4 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3" data-testid="reply-sent-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-100">
            <div className="font-medium">Reply sent.</div>
            <div className="text-xs text-emerald-100/70">The customer will receive your message via email. Send another below if you have more to add.</div>
          </div>
        </div>
      )}

      {/* Compose */}
      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5" data-testid="reply-compose">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="w-4 h-4 text-emerald-400" />
          <span className="label-mono">Your Reply</span>
        </div>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          placeholder={`Hi ${ctx.customer_name?.split(" ")[0] || ""},\n\nThanks for the handoff. Here's what I found on this listing…`}
          className="bg-[#050507] border-[#1A1A24] text-white text-sm"
          data-testid="reply-body-input"
          disabled={busy}
        />
        <div className="flex items-center justify-between mt-4">
          <div className="text-[11px] text-gray-500">
            Sends via email to <span className="text-gray-300 font-mono">{ctx.customer_email}</span>. Every reply is saved in the lead record.
          </div>
          <button
            onClick={submit}
            disabled={busy || !body.trim()}
            className="px-6 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="reply-send-btn"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send Reply"}
          </button>
        </div>
      </div>

      <p className="text-[10px] text-gray-600 mt-6 text-center">
        This link is valid for 7 days from when the notification email was sent. Anyone with this URL can reply on this specific lead only.
      </p>
    </div>
  );
}
